import * as React from 'react'
import { config, Hidden } from 'react-awesome-styled-grid'
import { useSelector } from 'react-redux'
import { FadeInImage } from '@components/shared/FadeInImage'
import type { TrackSeriesInfoType, TrackPropsType } from '../../models/Track'
import { convertTrackByIdToProps, convertTrackBySlugToPropsAsync, getComingSoonText } from '../../models/Track'
import Link from 'next/link'
import styled from 'styled-components'
import { colors } from '@components/shared'
import { useRouter } from 'next/router'
import { TagLinksRow } from '@components/Tag/TagLinksRow'
import PlayIndicator from '@components/Track/PlayIndicator'
import { GoodSansH1, GoodSansB4, GoodSansB2, GoodSansB3 } from '@components/shared/typography'
import type { AppStateType } from '../../reducers/appstate'
import { PlayButton } from '@components/Track/PlayButton'
import type { UserListenType } from '../../flowTypes'
import BadgeTextFloat from '@components/BadgeText'

type TrackListItemComponentPropsType = TrackPropsType & {
  playIndicator?: boolean,
  showCreator?: boolean,
  hideSeriesInfo?: boolean
}

export function TrackListItemComponent (props: TrackListItemComponentPropsType): React.ReactNode {
  const {
    id,
    title,
    images,
    description,
    series,
    tags,
    slug,
    showCreator,
    creator,
    hideSeriesInfo,
    popularity
  } = props
  const router = useRouter()
  const click = React.useCallback((): void => {
    router.push(props.slug)
  }, [props.slug])

  const isSignedIn = useSelector((state: AppStateType): boolean => !!(state.user || {}).id)
  const isSeries = !!series.length && !hideSeriesInfo
  const isListened = useSelector((state: AppStateType): UserListenType | null | undefined => {
    return state.user?.listens?.[props.id]
  })

  const comingSoon = getComingSoonText(props.publishedAt)

  const renderLength = (): React.ReactNode => {
    return (
      <LengthAndPlayIndicator>
        {props.lengthText}
        {props.playIndicator && isListened
          ? <PlayIndicatorContainer><PlayIndicator trackId={props.id} duration={props.duration} completed /></PlayIndicatorContainer>
          : !!popularity && popularity > 0 ? ` | ${popularity.toLocaleString()} plays` : ''
        }
      </LengthAndPlayIndicator>
    )
  }

  let formattedTitle = !isSeries ? `${(series?.[0]?.episodeInfo || '').replace(' |', ':')} ${title}` : title
  if (title?.includes('Chapter') || title?.includes(':')) {
    formattedTitle = title
  }
  return (
    <Wrapper>
      <TrackInfoContainer>
        <Flex>
          <ImageContainer onClick={click}>
            <Hidden xs={true}>
              {comingSoon && <BadgeTextFloat>{comingSoon}</BadgeTextFloat>}
            </Hidden>
            <FadeInImage src={images.img500} width={500} height={500} fill={false} />
          </ImageContainer>

          <TextContainer>
            <TitleLink href={slug} passHref={true}>
              {formattedTitle}
            </TitleLink>

            {isSeries
              ? <SeriesContainer>
                {series.map((seriesItem: TrackSeriesInfoType, index: number): React.ReactNode => {
                  return <SeriesInfo key={`${seriesItem.id}-${id}-${index}`}>{seriesItem.episodeInfo} <Link href={seriesItem.slug} passHref>{seriesItem.title}</Link></SeriesInfo>
                })}
              </SeriesContainer>
              : <SeriesInfo>{renderLength()}</SeriesInfo>
            }

            {(creator && showCreator !== false) && (
              <SeriesInfo>Created by <Link href={creator.slug} passHref>{creator.title}</Link></SeriesInfo>
            )}

            {/* Descriptions run long enough to dominate a phone-width row, so
                they only appear from md up. */}
            <Hidden xs sm >
              <Description>{description}</Description>
            </Hidden>

            {isSeries && (
              <LengthInfo color={colors.white70}>{renderLength()}</LengthInfo>
            )}

            <Hidden xs >
              {isSignedIn && <TagRowContainer><TagLinksRow id={id} tags={tags} /></TagRowContainer>}
            </Hidden>
          </TextContainer>
        </Flex>

        <PlayButton {...props} isIconStyle />
      </TrackInfoContainer>

      <Hidden sm md lg xl>
        <MobileDescription>
          {comingSoon }
        </MobileDescription>
      </Hidden>
      <Hidden sm md lg xl>
        {isSignedIn && <TagRowContainer><TagLinksRow id={id} tags={tags} /></TagRowContainer>}
      </Hidden>
    </Wrapper>
  )
}
const Flex = styled.div<any>`
  display: flex;
  flex: 1;

  ${(props: any): any => config(props).media.md`
    align-items: center;
  `}
`
const TrackInfoContainer = styled.div<any>`
  display: flex;
  flex-direction: row;
  gap: .5rem;
  align-items: flex-start;

  ${(props: any): any => config(props).media.md`
    gap: 2rem;
    align-items: center;
  `}
`
const Wrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  gap: .5rem;
`
const TextContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  gap: .5rem;
  ${(props: any): any => config(props).media.md`
    gap: .8rem;
  `}
  ${(props: any): any => config(props).media.lg`
    gap: 1rem;
  `}
`
const ImageContainer = styled.div<any>`
  width: 64px;
  height: 64px;
  margin-right: 16px;
  flex-shrink: 0;
    position: relative;
  img {
    border-radius: 10px;
  }
  ${(props: any): any => config(props).media.sm`
    width: 100px;
    height: 100px;
  `}
  ${(props: any): any => config(props).media.md`
    width: 200px;
    height: 200px;
    margin-right: 35px;
    img {
      border-radius: 20px;
    }
  `}
  ${(props: any): any => config(props).media.lg`
    width: 250px;
    height: 250px;
  `}
  :hover {
    opacity: 0.5;
  }
`

const TitleLink = styled(Link)<any>`
  ${GoodSansH1};
  line-height: 120%;
  text-decoration: none;
  font-size: 16px;
  ${(props: any): any => config(props).media.md`
    font-size: 20px;
  `}
`
/* Below md the length and tag lines are the only supporting text left on the row
   — the description is hidden — so they step up 2px to stay readable. Written as
   max-width queries so the lg sizes inside the GoodSans scales still win. */
const MOBILE_ONLY = '@media (max-width: 767px)'

const LengthInfo = styled.div<any>`
  ${GoodSansB4};
  color: ${(props: any): string => props.color || colors.white};
  ${MOBILE_ONLY} {
    font-size: 13px;
  }
`

const TagRowContainer = styled.div<any>`
  ${MOBILE_ONLY} {
    a {
      font-size: 14px;
    }
  }
`
const SeriesContainer = styled.div<any>`
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
  div:not(:last-child):after {
    content: ',';
    color: ${colors.orange};
  }
`
const SeriesInfo = styled.div<any>`
  ${GoodSansB3};
  line-height: 100%;
  color: ${colors.white};
  a {
    color: ${colors.orange};
  }
  ${MOBILE_ONLY} {
    font-size: 14px;
  }
`
const Description = styled.div<any>`
  ${GoodSansB2};
  color: ${colors.white70};
`
const MobileDescription = styled.div<any>`
  ${GoodSansB3};
  color: ${colors.white70};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
`
const LengthAndPlayIndicator = styled.div<any>`
  display: flex;
  align-items: center;
`
const PlayIndicatorContainer = styled.div<any>`
  width: 100px;
  margin-left: 16px;
  ${(props: any): any => config(props).media.md`
    width: 150px;
  `}
`

export function TrackListItem (props: { id: string, type: 'slug' | 'id' }): React.ReactNode {
  const [track, setTrack] = React.useState<any>(null)
  React.useEffect((): void => {
    if (props.type === 'id') {
      convertTrackByIdToProps(props.id).then((track: TrackPropsType): void => {
        setTrack(track)
      })
    } else {
      convertTrackBySlugToPropsAsync(props.id).then((track: TrackPropsType): void => {
        setTrack(track)
      })
    }
  }, [props.id])
  if (track) {
    return <TrackListItemComponent {...track} />
  }
  return null
}
